/**
 * WeekCalendar component
 * Weekly calendar grid with day cells for availability selection
 */

import { DayCell } from './DayCell';
import { getWeekDays, formatDateForAPI } from '@/lib/utils/date';
import type { BulkAvailabilityItem } from '@/types/api';

interface WeekCalendarProps {
  weekStart: Date;
  availability: BulkAvailabilityItem[];
  onChange: (availability: BulkAvailabilityItem[]) => void;
}

/**
 * WeekCalendar component
 *
 * Features:
 * - Display 7-day week grid
 * - Manage lunch/dinner availability per day
 * - Update state on toggle
 * - Highlight today
 * - Responsive grid layout
 *
 * @example
 * ```tsx
 * const [availability, setAvailability] = useState<BulkAvailabilityItem[]>([]);
 *
 * <WeekCalendar
 *   weekStart={weekStart}
 *   availability={availability}
 *   onChange={setAvailability}
 * />
 * ```
 */
export function WeekCalendar({
  weekStart,
  availability,
  onChange,
}: WeekCalendarProps) {
  const weekDays = getWeekDays(weekStart);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  /**
   * Get availability state for a specific date and meal type
   */
  const getAvailabilityState = (date: string, mealType: 'lunch' | 'dinner'): boolean => {
    const item = availability.find(
      (a) => a.date === date && a.mealType === mealType
    );
    return item?.isPresent ?? false;
  };

  /**
   * Update availability for a specific date and meal type
   */
  const handleToggle = (date: string, mealType: 'lunch' | 'dinner', isPresent: boolean) => {
    const existingIndex = availability.findIndex(
      (a) => a.date === date && a.mealType === mealType
    );

    let newAvailability: BulkAvailabilityItem[];

    if (existingIndex >= 0) {
      // Update existing entry
      newAvailability = [...availability];
      newAvailability[existingIndex] = { date, mealType, isPresent };
    } else {
      // Add new entry
      newAvailability = [...availability, { date, mealType, isPresent }];
    }

    onChange(newAvailability);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
      {weekDays.map((day) => {
        const dateString = formatDateForAPI(day);
        const isToday = day.getTime() === today.getTime();
        // Disable past days (before today, excluding today)
        const isPast = day < today;

        return (
          <DayCell
            key={dateString}
            date={day}
            lunchChecked={getAvailabilityState(dateString, 'lunch')}
            dinnerChecked={getAvailabilityState(dateString, 'dinner')}
            onLunchChange={(checked) => handleToggle(dateString, 'lunch', checked)}
            onDinnerChange={(checked) => handleToggle(dateString, 'dinner', checked)}
            isToday={isToday}
            isPast={isPast}
          />
        );
      })}
    </div>
  );
}
